import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Link } from "@corpuscomplete/web/components/link";
import { Route } from "@corpuscomplete/web/routes/_authenticated/groups/$id.manage";
import { Button } from "@corpuscomplete/web-ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@corpuscomplete/web-ui/components/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@corpuscomplete/web-ui/components/dropdown-menu";
import { Label } from "@corpuscomplete/web-ui/components/label";
import { RadioGroup, RadioGroupItem } from "@corpuscomplete/web-ui/components/radio-group";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { Page, PageBody, PageContent, PageHeader } from "@corpuscomplete/web/components/layout";
import { useAuth } from "@corpuscomplete/web/auth/hooks";
import { Space } from "@corpuscomplete/core/auth/space";
import { GroupUI } from "@corpuscomplete/core-ui/auth/group";
import { GroupManage } from "@corpuscomplete/core-ui/auth/group/manage";
import { Avatar, AvatarFallback, AvatarImage } from "@corpuscomplete/web-ui/components/avatar";
import { ChevronLeftIcon, MoreHorizontalIcon, UserMinusIcon } from "lucide-react";

function ChangePermissionsDialog() {
  const { userId } = GroupManage.useContext();
  const myGroupGrants = GroupManage.useMyGroupGrants();
  const { grantsByUserId, ownerCount } = GroupManage.useGrants();
  const target = GroupManage.useDialogStore((s) => s.changePermissionsTarget);
  const newRootRole = GroupManage.useDialogStore((s) => s.newRootRole);
  const newMembersRole = GroupManage.useDialogStore((s) => s.newMembersRole);
  const setNewRootRole = GroupManage.useDialogStore((s) => s.setNewRootRole);
  const setNewMembersRole = GroupManage.useDialogStore((s) => s.setNewMembersRole);
  const closeDialog = GroupManage.useDialogStore((s) => s.closeChangePermissions);
  const { setRootGrant, setMembersGrant } = GroupManage.useMutations();

  const isSelf = target?.userId === userId;
  const memberGrants = target ? (grantsByUserId.get(target.userId) ?? []) : [];

  const handleSave = () => {
    if (!target) return;
    const currentRootRole = GroupUI.getMemberRootRole(memberGrants);
    const currentMembersRole = GroupUI.getMemberMembersRole(memberGrants);
    if (newRootRole !== currentRootRole) {
      setRootGrant(target.userId, newRootRole);
    }
    if (newMembersRole !== currentMembersRole) {
      setMembersGrant(target.userId, newMembersRole);
    }
    closeDialog();
  };

  const allowedChanges = target
    ? isSelf
      ? Space.getSelfRoleChanges(myGroupGrants, ownerCount)
      : Space.getAllowedRoleChanges(myGroupGrants, memberGrants)
    : { rootRoles: [], contentsRoles: [], canModify: false };

  const actorIsOwner = Space.getGrant(myGroupGrants, "root")?.role === "owner";
  const limitationText = isSelf
    ? "You can only demote yourself. Self-promotion is not allowed."
    : !actorIsOwner
      ? "As an editor, you can only assign viewer roles."
      : null;

  const rootRoleDisabled = (role: Space.RootRole) => !allowedChanges.rootRoles.includes(role);
  const membersRoleDisabled = (role: Space.ContentsRole) =>
    !allowedChanges.contentsRoles.includes(role);

  return (
    <Dialog open={target !== null} onOpenChange={closeDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isSelf ? "Change Your Permissions" : "Change Permissions"}</DialogTitle>
          <DialogDescription>
            {isSelf
              ? "Adjust your own permissions in this group."
              : target?.profile?.name
                ? `Set permissions for ${target.profile.name}`
                : "Set permissions for this member"}
          </DialogDescription>
        </DialogHeader>
        {limitationText && (
          <Text variant="xs" className="text-muted-foreground bg-muted px-3 py-2 rounded-md">
            {limitationText}
          </Text>
        )}
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <Label className="text-sm font-medium">Group Role</Label>
            <Text variant="xs" className="text-muted-foreground mb-1">
              Controls access to view and manage the group itself
            </Text>
            <RadioGroup
              value={newRootRole ?? "none"}
              onValueChange={(v) => setNewRootRole(v === "none" ? null : (v as Space.RootRole))}
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem
                  value="owner"
                  id="root-owner"
                  disabled={rootRoleDisabled("owner")}
                />
                <Label
                  htmlFor="root-owner"
                  className={rootRoleDisabled("owner") ? "font-normal opacity-50" : "font-normal"}
                >
                  Owner
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem
                  value="editor"
                  id="root-editor"
                  disabled={rootRoleDisabled("editor")}
                />
                <Label
                  htmlFor="root-editor"
                  className={rootRoleDisabled("editor") ? "font-normal opacity-50" : "font-normal"}
                >
                  Editor
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem
                  value="viewer"
                  id="root-viewer"
                  disabled={rootRoleDisabled("viewer")}
                />
                <Label
                  htmlFor="root-viewer"
                  className={rootRoleDisabled("viewer") ? "font-normal opacity-50" : "font-normal"}
                >
                  Viewer
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="none" id="root-none" disabled={rootRoleDisabled(null)} />
                <Label
                  htmlFor="root-none"
                  className={
                    rootRoleDisabled(null)
                      ? "font-normal text-muted-foreground opacity-50"
                      : "font-normal text-muted-foreground"
                  }
                >
                  None
                </Label>
              </div>
            </RadioGroup>
          </div>
          <div className="flex flex-col gap-2">
            <Label className="text-sm font-medium">Member Management</Label>
            <Text variant="xs" className="text-muted-foreground mb-1">
              Controls ability to invite, remove, and manage other members
            </Text>
            <RadioGroup
              value={newMembersRole ?? "none"}
              onValueChange={(v) =>
                setNewMembersRole(v === "none" ? null : (v as Space.ContentsRole))
              }
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem
                  value="editor"
                  id="members-editor"
                  disabled={membersRoleDisabled("editor")}
                />
                <Label
                  htmlFor="members-editor"
                  className={
                    membersRoleDisabled("editor") ? "font-normal opacity-50" : "font-normal"
                  }
                >
                  Editor
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem
                  value="viewer"
                  id="members-viewer"
                  disabled={membersRoleDisabled("viewer")}
                />
                <Label
                  htmlFor="members-viewer"
                  className={
                    membersRoleDisabled("viewer") ? "font-normal opacity-50" : "font-normal"
                  }
                >
                  Viewer
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem
                  value="none"
                  id="members-none"
                  disabled={membersRoleDisabled(null)}
                />
                <Label
                  htmlFor="members-none"
                  className={
                    membersRoleDisabled(null)
                      ? "font-normal text-muted-foreground opacity-50"
                      : "font-normal text-muted-foreground"
                  }
                >
                  None
                </Label>
              </div>
            </RadioGroup>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={closeDialog}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RemoveMemberDialog({
  member,
  onClose,
  onConfirm,
}: {
  member: GroupUI.Member | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={member !== null} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Remove Member</DialogTitle>
          <DialogDescription>
            Are you sure you want to remove {member?.profile?.name ?? "this member"}? They will lose
            access to all shared content.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm}>
            Remove
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RoleMemberRow({ memberWithGrants }: { memberWithGrants: GroupUI.MemberWithGrants }) {
  const { userId } = GroupManage.useContext();
  const myGroupGrants = GroupManage.useMyGroupGrants();
  const { ownerCount } = GroupManage.useGrants();
  const permissions = GroupManage.usePermissions();
  const openChangePermissions = GroupManage.useDialogStore((s) => s.openChangePermissions);
  const { removeMember } = GroupManage.useMutations();
  const [removeMemberTarget, setRemoveMemberTarget] = useState<GroupUI.Member | null>(null);

  const { member, rootRole, membersRole, grants } = memberWithGrants;
  const isSelf = member.userId === userId;
  const roleDisplay = GroupUI.formatRoleDisplay(rootRole, membersRole);

  const selfChanges = Space.getSelfRoleChanges(myGroupGrants, ownerCount);
  const otherChanges = Space.getAllowedRoleChanges(myGroupGrants, grants);

  const canSelfDemote = isSelf && selfChanges.canModify;
  const canChangeOtherPerms = !isSelf && otherChanges.canModify && permissions.canSetRootGrant;
  const canRemove = !isSelf && permissions.canRemoveMembers;

  const showMenu = canSelfDemote || canChangeOtherPerms || canRemove;

  const handleOpenChangePermissions = () => {
    openChangePermissions(member, grants);
  };

  const handleRemove = () => {
    removeMember(member.userId);
    setRemoveMemberTarget(null);
  };

  return (
    <>
      <div className="flex items-center justify-between gap-3 rounded-md border p-3">
        <div className="flex items-center gap-3">
          <Avatar>
            <AvatarImage src={member.profile?.image || undefined} />
            <AvatarFallback>{member.profile?.name.charAt(0).toUpperCase() ?? "?"}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <Text variant="sm">{member.profile?.name ?? "Unknown"}</Text>
              {isSelf && (
                <Text variant="xs" className="text-muted-foreground">
                  (You)
                </Text>
              )}
            </div>
            {roleDisplay && (
              <Text variant="xs" className="text-muted-foreground">
                {roleDisplay}
              </Text>
            )}
          </div>
        </div>
        {showMenu && (
          <DropdownMenu>
            <DropdownMenuTrigger render={<Button variant="ghost" size="icon-sm" />}>
              <MoreHorizontalIcon className="size-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {(canSelfDemote || canChangeOtherPerms) && (
                <DropdownMenuItem onClick={handleOpenChangePermissions}>
                  Change Permissions
                </DropdownMenuItem>
              )}
              {(canSelfDemote || canChangeOtherPerms) && canRemove && <DropdownMenuSeparator />}
              {canRemove && (
                <DropdownMenuItem
                  variant="destructive"
                  onClick={() => setRemoveMemberTarget(member)}
                >
                  <UserMinusIcon className="mr-2 size-4" />
                  Remove
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <RemoveMemberDialog
        member={removeMemberTarget}
        onClose={() => setRemoveMemberTarget(null)}
        onConfirm={handleRemove}
      />
    </>
  );
}

function BasicMemberRow({ member }: { member: GroupUI.Member }) {
  const { userId } = GroupManage.useContext();
  const { grantsByUserId } = GroupManage.useGrants();
  const permissions = GroupManage.usePermissions();
  const openChangePermissions = GroupManage.useDialogStore((s) => s.openChangePermissions);

  const isSelf = member.userId === userId;
  const grants = grantsByUserId.get(member.userId) ?? [];

  const handleAddRole = () => {
    openChangePermissions(member, grants);
  };

  return (
    <div className="flex items-center justify-between gap-3 rounded-md border p-3">
      <div className="flex items-center gap-3">
        <Avatar>
          <AvatarImage src={member.profile?.image || undefined} />
          <AvatarFallback>{member.profile?.name.charAt(0).toUpperCase() ?? "?"}</AvatarFallback>
        </Avatar>
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <Text variant="sm">{member.profile?.name ?? "Unknown"}</Text>
            {isSelf && (
              <Text variant="xs" className="text-muted-foreground">
                (You)
              </Text>
            )}
          </div>
          <Text variant="xs" className="text-muted-foreground">
            No roles assigned
          </Text>
        </div>
      </div>
      {permissions.canSetRootGrant && (
        <Button variant="outline" size="sm" onClick={handleAddRole}>
          Add Role
        </Button>
      )}
    </div>
  );
}

function GroupManageContent() {
  const navigate = useNavigate();
  const { groupId } = GroupManage.useContext();
  const group = GroupManage.useGroup();
  const { isLoading } = GroupManage.useLoadingState();
  const permissions = GroupManage.usePermissions();
  const roleMembers = GroupManage.useRoleMembers();
  const basicMembers = GroupManage.useBasicMembers();

  if (isLoading) {
    return null;
  }

  if (!group) {
    void navigate({ to: "/groups" });
    return null;
  }

  if (!permissions.canViewMemberGrants) {
    void navigate({ to: "/groups/$id", params: { id: groupId } });
    return null;
  }

  return (
    <Page>
      <PageBody>
        <PageHeader>
          <Link
            to="/groups/$id"
            params={{ id: groupId }}
            className="mr-auto flex items-center gap-2"
          >
            <ChevronLeftIcon className="size-4" />
            <Text>Manage</Text>
          </Link>
        </PageHeader>
        <PageContent>
          <div className="flex flex-col gap-6">
            <section>
              <Text variant="sm" className="mb-3 font-medium">
                Roles ({roleMembers.length})
              </Text>
              <div className="flex flex-col gap-2">
                {roleMembers.map((mwg) => (
                  <RoleMemberRow key={mwg.member.userId} memberWithGrants={mwg} />
                ))}
                {roleMembers.length === 0 && (
                  <Text variant="sm" className="text-muted-foreground py-4 text-center">
                    No members have roles assigned
                  </Text>
                )}
              </div>
            </section>

            <section>
              <Text variant="sm" className="mb-3 font-medium">
                Basic Members ({basicMembers.length})
              </Text>
              <div className="flex flex-col gap-2">
                {basicMembers.map((member) => (
                  <BasicMemberRow key={member.userId} member={member} />
                ))}
                {basicMembers.length === 0 && (
                  <Text variant="sm" className="text-muted-foreground py-4 text-center">
                    No basic members
                  </Text>
                )}
              </div>
            </section>
          </div>
        </PageContent>
      </PageBody>

      <ChangePermissionsDialog />
    </Page>
  );
}

export function GroupManagePage() {
  const { id: groupId } = Route.useParams();
  const { userId } = useAuth();

  return (
    <GroupManage.DataProvider groupId={groupId} userId={userId}>
      <GroupManageContent />
    </GroupManage.DataProvider>
  );
}
